import { useEffect, useState } from 'react';
import {
  Badge,
  ActionIcon,
  Box,
  Button,
  Card,
  Group,
  Pagination,
  Paper,
  Progress,
  SimpleGrid,
  Stack,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconChevronLeft, IconChevronRight, IconClock, IconHelp, IconPencil, IconUser, IconX } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { USER_RIGHTS } from '../constants/userRights';
import { useMediaQuery } from '@mantine/hooks';
import dayjs from 'dayjs';
import HttpClient from '../helpers/HttpClient';
import {
  ParticipantResponse,
  TrainingEvent,
  TrainingEventStatus,
  TrainingEventType,
  TrainingParticipant,
} from '../types/training-event.type';
import { Person } from '../types/person.type';
import { useUserContext } from '../providers/UserContext';

const PAGE_SIZE = 4;

const TYPE_COLOR: Record<TrainingEventType, string> = {
  [TrainingEventType.TRAINING]: 'teal',
  [TrainingEventType.MATCH]: 'indigo',
  [TrainingEventType.EVENT]: 'orange',
};

const TYPE_LABEL: Record<TrainingEventType, string> = {
  [TrainingEventType.TRAINING]: 'Training',
  [TrainingEventType.MATCH]: 'Spiel',
  [TrainingEventType.EVENT]: 'Event',
};

function fmtTime(minutes: number): string {
  const h = Math.floor(minutes / 60) % 24;
  const m = minutes % 60;
  return `${h}:${String(m).padStart(2, '0')}`;
}

function personName(p?: Person): string {
  return p ? `${p.firstName} ${p.lastName}` : '';
}

function relativeDay(date: string): string {
  const diff = dayjs(date).startOf('day').diff(dayjs().startOf('day'), 'day');
  if (diff === 0) return 'Heute';
  if (diff === 1) return 'Morgen';
  return `in ${diff} Tagen`;
}

interface Props {
  personId: number;
  label: string;
  trainings: TrainingEvent[];
}

interface UpcomingItem {
  training: TrainingEvent;
  participantId: number;
  response: ParticipantResponse;
  // Several people can answer for a child (the child, each parent) — the
  // last answer wins, so show who gave it.
  respondedByName?: string;
}

// Derives "my upcoming, respondable trainings" from the trainings Home.tsx
// already fetches for its calendar — no dedicated filtered endpoint exists
// (or is needed) for this.
export const TrainingResponseList = ({ personId, label, trainings }: Props) => {
  const [items, setItems] = useState<UpcomingItem[]>([]);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const { currentPerson } = useUserContext();
  // Mobile: one week at a time (current week first, forward only — past
  // trainings can't be answered anyway). Desktop keeps the paged grid.
  const isMobile = useMediaQuery('(max-width: 48em)', undefined, { getInitialValueInEffect: false }) ?? false;
  const [weekOffset, setWeekOffset] = useState(0);

  useEffect(() => {
    const today = dayjs().format('YYYY-MM-DD');
    const upcoming: UpcomingItem[] = [];
    for (const training of trainings) {
      if (training.isCancelled || training.status === TrainingEventStatus.GRADED) continue;
      if (training.date < today) continue;
      const participant = training.participants?.find((p) => p.person?.id === personId);
      if (!participant) continue;
      upcoming.push({
        training,
        participantId: participant.id,
        response: participant.response,
        respondedByName: participant.respondedByName,
      });
    }
    upcoming.sort(
      (a, b) => a.training.date.localeCompare(b.training.date) || a.training.startTime - b.training.startTime,
    );
    setItems(upcoming);
  }, [trainings, personId]);

  const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  // Clamp if the list shrank (e.g. trainings reloaded) while on a later page.
  const currentPage = Math.min(page, totalPages);

  const today = dayjs().startOf('day');
  const weekStart = today.subtract((today.day() + 6) % 7, 'day').add(weekOffset, 'week'); // Monday
  const weekEnd = weekStart.add(6, 'day');
  const weekStartKey = weekStart.format('YYYY-MM-DD');
  const weekEndKey = weekEnd.format('YYYY-MM-DD');
  const hasLaterWeeks = items.some((i) => i.training.date > weekEndKey);

  const pageItems = isMobile
    ? items.filter((i) => i.training.date >= weekStartKey && i.training.date <= weekEndKey)
    : items.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const respond = async (item: UpcomingItem, response: ParticipantResponse) => {
    setSavingId(item.participantId);
    const previous = { response: item.response, respondedByName: item.respondedByName };
    const myName = currentPerson ? `${currentPerson.firstName} ${currentPerson.lastName}`.trim() : undefined;
    setItems((prev) =>
      prev.map((i) => (i.participantId === item.participantId ? { ...i, response, respondedByName: myName } : i)),
    );
    try {
      const client = new HttpClient<TrainingParticipant>(
        `/training-event/${item.training.id}/participant/${item.participantId}`,
      );
      await client.patchAction('response', { response });
    } catch {
      setItems((prev) =>
        prev.map((i) => (i.participantId === item.participantId ? { ...i, ...previous } : i)),
      );
      notifications.show({ color: 'red', message: 'Antwort konnte nicht gespeichert werden.' });
    } finally {
      setSavingId(null);
    }
  };

  if (items.length === 0) return null;

  return (
    <Paper shadow="sm" p="md" radius="md" withBorder data-tour="dashboard-my-trainings">
      <Group justify="space-between" mb="sm" wrap="wrap" gap="xs">
        <Group gap="xs">
          <Title order={4}>{label}</Title>
          <Badge variant="light" color="gray" size="sm">{items.length}</Badge>
        </Group>
        {isMobile ? (
          <Group gap={4} wrap="nowrap">
            <ActionIcon variant="subtle" aria-label="vorherige Woche" disabled={weekOffset === 0} onClick={() => setWeekOffset((w) => w - 1)}>
              <IconChevronLeft size={18} />
            </ActionIcon>
            <Text size="xs" c="dimmed">
              {weekOffset === 0 ? 'Diese Woche' : `${weekStart.format('DD.MM.')} – ${weekEnd.format('DD.MM.')}`}
            </Text>
            <ActionIcon variant="subtle" aria-label="nächste Woche" disabled={!hasLaterWeeks} onClick={() => setWeekOffset((w) => w + 1)}>
              <IconChevronRight size={18} />
            </ActionIcon>
          </Group>
        ) : (
          totalPages > 1 && (
            <Pagination size="sm" total={totalPages} value={currentPage} onChange={setPage} />
          )
        )}
      </Group>
      {isMobile && pageItems.length === 0 && (
        <Text size="sm" c="dimmed">Keine Trainings in dieser Woche.</Text>
      )}
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="sm">
        {pageItems.map((item) => (
          <EventCard
            key={item.participantId}
            item={item}
            saving={savingId === item.participantId}
            onRespond={(r) => respond(item, r)}
          />
        ))}
      </SimpleGrid>
    </Paper>
  );
};

interface EventCardProps {
  item: UpcomingItem;
  saving: boolean;
  onRespond: (response: ParticipantResponse) => void;
}

function EventCard({ item, saving, onRespond }: EventCardProps) {
  const { training, participantId, response, respondedByName } = item;
  // Trainers (training right) can jump straight into editing the event.
  const { userRights } = useUserContext();
  const canEdit = userRights.includes(USER_RIGHTS.TRAINING);
  const navigate = useNavigate();
  const color = TYPE_COLOR[training.eventType] ?? 'teal';
  const date = new Date(`${training.date}T00:00:00`);
  const weekday = date.toLocaleDateString('de-DE', { weekday: 'short' }).replace('.', '');
  const month = date.toLocaleDateString('de-DE', { month: 'short' }).replace('.', '');

  // Count responses with the user's own (possibly optimistically updated)
  // answer substituted, so the numbers move as soon as they click.
  const counts = { confirmed: 0, declined: 0, pending: 0 };
  for (const p of training.participants ?? []) {
    const r = p.id === participantId ? response : p.response;
    if (r === ParticipantResponse.CONFIRMED) counts.confirmed++;
    else if (r === ParticipantResponse.DECLINED) counts.declined++;
    else counts.pending++;
  }
  const total = counts.confirmed + counts.declined + counts.pending;

  const trainers = [training.assignedPerson, ...(training.coTrainers ?? [])]
    .filter(Boolean)
    .map(personName)
    .join(', ');

  const statusBadge =
    response === ParticipantResponse.CONFIRMED ? (
      <Badge size="sm" color="green" variant="light" leftSection={<IconCheck size={12} />}>Zugesagt</Badge>
    ) : response === ParticipantResponse.DECLINED ? (
      <Badge size="sm" color="red" variant="light" leftSection={<IconX size={12} />}>Abgesagt</Badge>
    ) : (
      <Badge size="sm" color="gray" variant="light" leftSection={<IconHelp size={12} />}>Offen</Badge>
    );

  return (
    <Card
      withBorder
      radius="md"
      padding={0}
      style={{ borderLeft: `4px solid var(--mantine-color-${color}-6)` }}
    >
      <Group align="stretch" gap={0} wrap="nowrap" style={{ flex: 1 }}>
        <Stack
          gap={0}
          align="center"
          justify="center"
          px="sm"
          py="xs"
          miw={64}
          style={{ background: `var(--mantine-color-${color}-light)` }}
        >
          <Text size="xs" fw={600} c={color} tt="uppercase">{weekday}</Text>
          <Text size="xl" fw={800} lh={1.1}>{date.getDate()}</Text>
          <Text size="xs" c="dimmed" tt="uppercase">{month}</Text>
        </Stack>

        <Stack gap={6} p="sm" style={{ flex: 1, minWidth: 0 }}>
          <Group justify="space-between" gap={4} wrap="nowrap">
            <Group gap={4} wrap="wrap" style={{ minWidth: 0 }}>
              <Badge size="xs" color={color} variant="filled">{TYPE_LABEL[training.eventType]}</Badge>
              {(training.tags ?? []).map((tag) => (
                <Badge key={tag.id} size="xs" color={tag.color || 'teal'} variant="light">{tag.name}</Badge>
              ))}
            </Group>
            <Group gap={2} wrap="nowrap" style={{ flexShrink: 0 }}>
              <Text size="xs" c="dimmed">{relativeDay(training.date)}</Text>
              {canEdit && (
                <Tooltip label="Bearbeiten">
                  <ActionIcon
                    size="sm"
                    variant="subtle"
                    aria-label="Termin bearbeiten"
                    onClick={() => navigate(`/training/events/edit/${training.id}`)}
                  >
                    <IconPencil size={14} />
                  </ActionIcon>
                </Tooltip>
              )}
            </Group>
          </Group>

          <Text fw={600} lineClamp={2}>
            {training.notes || TYPE_LABEL[training.eventType]}
          </Text>

          <Group gap={4} wrap="nowrap">
            <IconClock size={14} style={{ flexShrink: 0 }} />
            <Text size="sm" c="dimmed">
              {fmtTime(training.startTime)}
              {training.duration ? ` – ${fmtTime(training.startTime + training.duration)}` : ''} Uhr
            </Text>
          </Group>

          {trainers && (
            <Group gap={4} wrap="nowrap">
              <IconUser size={14} style={{ flexShrink: 0 }} />
              <Text size="sm" c="dimmed" truncate>{trainers}</Text>
            </Group>
          )}

          {total > 0 && (
            <Box>
              <Group gap="xs" mb={4}>
                <Tooltip label="Zusagen">
                  <Group gap={2}>
                    <IconCheck size={14} color="var(--mantine-color-green-6)" />
                    <Text size="xs" fw={600}>{counts.confirmed}</Text>
                  </Group>
                </Tooltip>
                <Tooltip label="Absagen">
                  <Group gap={2}>
                    <IconX size={14} color="var(--mantine-color-red-6)" />
                    <Text size="xs" fw={600}>{counts.declined}</Text>
                  </Group>
                </Tooltip>
                <Tooltip label="Offen">
                  <Group gap={2}>
                    <IconHelp size={14} color="var(--mantine-color-gray-6)" />
                    <Text size="xs" fw={600}>{counts.pending}</Text>
                  </Group>
                </Tooltip>
                <Stack gap={0} align="flex-end" style={{ marginLeft: 'auto' }}>
                  {statusBadge}
                  {response !== ParticipantResponse.PENDING && respondedByName && (
                    <Text size="xs" c="dimmed">von {respondedByName}</Text>
                  )}
                </Stack>
              </Group>
              <Progress.Root size="sm">
                <Progress.Section value={(counts.confirmed / total) * 100} color="green" />
                <Progress.Section value={(counts.declined / total) * 100} color="red" />
                <Progress.Section value={(counts.pending / total) * 100} color="gray.3" />
              </Progress.Root>
            </Box>
          )}

          <Group gap="xs" grow mt="auto">
            <Button
              size="xs"
              color="green"
              variant={response === ParticipantResponse.CONFIRMED ? 'filled' : 'light'}
              leftSection={<IconCheck size={14} />}
              loading={saving}
              onClick={() => onRespond(ParticipantResponse.CONFIRMED)}
            >
              Zusagen
            </Button>
            <Button
              size="xs"
              color="red"
              variant={response === ParticipantResponse.DECLINED ? 'filled' : 'light'}
              leftSection={<IconX size={14} />}
              loading={saving}
              onClick={() => onRespond(ParticipantResponse.DECLINED)}
            >
              Absagen
            </Button>
          </Group>
        </Stack>
      </Group>
    </Card>
  );
}
